import type { MosaicBranch, MosaicNode } from 'react-mosaic-component'
import { getNodeAtPath } from 'react-mosaic-component'

/** The four edges react-mosaic's drop targets report. */
export type DropPosition = 'top' | 'bottom' | 'left' | 'right'

/**
 * What a drop from the Panel Hub should do to the tree, resolved from the
 * `{ path, position }` react-mosaic's drop targets hand back.
 *
 * - `split-panel`: the drop landed on a tile's edge; split that leaf.
 * - `split-root`: the drop landed on one of the four edges of the whole
 *   mosaic (react-mosaic's RootDropTargets report path `[]`); wrap the whole
 *   tree so the new panel takes that side.
 * - `append`: nothing to split (empty tree); insert wherever the default goes.
 */
export type DropResolution =
  | { kind: 'split-panel'; targetPanelId: string; placement: DropPosition }
  | { kind: 'split-root'; placement: DropPosition }
  | { kind: 'append' }

export function resolveDrop(
  tree: MosaicNode<string> | null,
  path: MosaicBranch[],
  position: DropPosition,
): DropResolution {
  if (tree == null) return { kind: 'append' }
  const node = getNodeAtPath(tree, path)
  if (typeof node === 'string') {
    // A root drop on a single-leaf tree resolves to that leaf, which splits
    // it exactly as a root wrap would.
    return { kind: 'split-panel', targetPanelId: node, placement: position }
  }
  if (path.length === 0) return { kind: 'split-root', placement: position }
  // A parent node below the root has no drop target of its own; treat a
  // stale path as "no target" rather than guessing a leaf.
  return { kind: 'append' }
}

/**
 * Wrap the whole tree so a new leaf takes one outer side: the tree
 * mutation behind a `split-root` resolution. Left/top put the new leaf first
 * (react-mosaic lays `first` out left or on top), right/bottom put it second.
 */
export function splitRoot(
  tree: MosaicNode<string>,
  newLeafId: string,
  placement: DropPosition,
): MosaicNode<string> {
  const direction = placement === 'left' || placement === 'right' ? 'row' : 'column'
  const leading = placement === 'left' || placement === 'top'
  return {
    direction,
    first: leading ? newLeafId : tree,
    second: leading ? tree : newLeafId,
  }
}
